import { Card,CardHeader,CardTitle,CardContent,CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { X,RefreshCw,Key,Unlink,MoreVertical,Pencil,Trash2,Monitor,Hash,Clock } from 'lucide-react'
import { DropdownMenu,DropdownMenuContent,DropdownMenuItem,DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import type { PrintAgent } from '@/types'
import { AgentStatusBadge } from './agent-status-badge'
import { formatDistanceToNow } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import { getPrintAgent } from '@/features/devices/api'
import { queryKeys } from '@/lib/query-keys'
import { formatInStoreTz } from '@/lib/time'

interface AgentDetailCardProps {
  agent: PrintAgent
  onClose: () => void
  onRegeneratePairCode: () => void
  onRegenerateKey: () => void
  onUnpair: () => void
  onEdit: () => void
  onDelete: () => void
}

export function AgentDetailCard({
  agent,
  onClose,
  onRegeneratePairCode,
  onRegenerateKey,
  onUnpair,
  onEdit,
  onDelete,
}: AgentDetailCardProps) {
  const { data: liveAgent } = useQuery({
    queryKey: queryKeys.printAgent(agent.id),
    queryFn: () => getPrintAgent(agent.id),
    refetchInterval: 10_000,
  })

  const displayAgent = liveAgent ?? agent

  return (
    <Card className="squircle w-full md:w-[320px] lg:w-[360px] sticky top-0 max-h-[calc(100vh-6.5rem)] overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 shadow-lg pb-4 flex flex-col shrink-0">
      <CardHeader className="flex flex-row items-start justify-between pb-4 sticky top-0 bg-card z-10 space-y-0">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base truncate" title={agent.name}>{agent.name}</CardTitle>
            <AgentStatusBadge status={displayAgent.status} dotOnly />
          </div>
          <CardDescription className="text-xs -mt-1">
            {displayAgent.isPaired ? 'Paired' : 'Unpaired'}
          </CardDescription>
        </div>
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-4 w-4" /> Edit Name
              </DropdownMenuItem>
              <DropdownMenuItem className="text-destructive focus:text-destructive focus:bg-destructive/10" onClick={onDelete}>
                <Trash2 className="h-4 w-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 overflow-y-auto pt-2 space-y-6">
        <div className="bg-muted/30 border rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Monitor className="h-3.5 w-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Platform</span>
            </div>
            <span className="text-xs font-mono">{displayAgent.platform || '—'}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Hash className="h-3.5 w-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Version</span>
            </div>
            <span className="text-xs font-mono">{displayAgent.version || '—'}</span>
          </div>
          
        </div>

        <div className="space-y-2">
          {!displayAgent.isPaired ? (
            <Button variant="outline" className="w-full justify-start" onClick={onRegeneratePairCode}>
              <RefreshCw className="mr-2 h-4 w-4" /> Regenerate Pair Code
            </Button>
          ) : (
            <>
              <Button variant="outline" className="w-full justify-start" onClick={onRegenerateKey}>
                <Key className="mr-2 h-4 w-4" /> Regenerate API Key
              </Button>
              <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" onClick={onUnpair}>
                <Unlink className="mr-2 h-4 w-4" /> Unpair Agent
              </Button>
            </>
          )}
        </div>

        <div className="pt-6 mt-auto border-t border-border/50 grid grid-cols-2 gap-4 text-xs text-muted-foreground">
          <div>
            <span className="font-medium text-foreground block mb-1">Created</span>
            {displayAgent.createdAt ? formatInStoreTz(displayAgent.createdAt, 'MMM d, yyyy') : '—'}
          </div>
          <div>
            <span className="font-medium text-foreground block mb-1">Activity</span>
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {displayAgent.lastSeenAt
                ? formatDistanceToNow(new Date(displayAgent.lastSeenAt), { addSuffix: true })
                : 'Never'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
